// apollo.ts
import { ApolloClient, InMemoryCache, HttpLink, Observable } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';  // ✅ add this
import { onError } from '@apollo/client/link/error';
import { useAuthStore } from '@/stores';
import { fetchWrapper } from '@/helpers';
import { isTokenExpired } from '@/helpers/token';

// The server answers an expired/missing login with code UNAUTHENTICATED --
// as a GraphQL error, or (HTTP 400) inside a network error's result. Until
// 2026-10-11 it sent a 500 INTERNAL_SERVER_ERROR instead, so this never
// matched and an expired session just kept failing.
function isUnauthenticated(graphQLErrors, networkError) {
  const errors = [
    ...(graphQLErrors || []),
    ...((networkError && networkError.result && networkError.result.errors) || []),
  ];
  return errors.some((e) => e && e.extensions && e.extensions.code === 'UNAUTHENTICATED')
    || (networkError && networkError.statusCode === 401);
}

// Derived from fetchWrapper.url so the REST base and the GraphQL endpoint
// always point at the same backend (edit VITE_API_URL / fetch-wrapper.js
// in one place instead of two).
const httpLink = new HttpLink({
  uri: `${fetchWrapper.url}graphql`,
  // ✅ NO custom fetch — let Apollo handle body/headers
});

// ✅ Auth headers via setContext (correct pattern)
const authLink = setContext((_, { headers }) => {
  const authStore = useAuthStore();
  let token = authStore.user?.token;
  // An expired token would only earn an UNAUTHENTICATED reply; log out now.
  if (token && isTokenExpired(token)) {
    authStore.logout();
    token = null;
  }
  const platform = localStorage.getItem('platform');

  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(platform ? { platform } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (isUnauthenticated(graphQLErrors, networkError)) {
    // Log out once and swallow the error: the page is being replaced by the
    // login screen, so passing it on would only print console errors.
    const authStore = useAuthStore();
    if (authStore.user) authStore.logout();
    return new Observable(() => {});
  }
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      console.error(`[GraphQL error]: ${error.message}`);
    });
  }
  if (networkError) {
    if (networkError.message && (networkError.message.includes('Store reset') || networkError.message.includes('store was reset'))) {
      return;
    }
    console.error(`[Network error]: ${networkError}`);
  }
});

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: errorLink.concat(authLink.concat(httpLink)), // ✅ correct order
});